import {z} from "zod";
import {
    ACTION_TYPES,
    Action,
    ArmorClass,
    Attribute,
    ATTRIBUTE_STATS,
    Character,
    HitPoints,
    Speeds,
    Spellcasting,
} from "@/characters/model/Character.ts";
import {imageSchema} from "@/images/ImageSchema.ts";
import {characterSensesSchema, emptyCharacterSenses, type CharacterSenses} from "@/characters/model/CharacterSenses.ts";

const hpSchema = z.object({
    value: z.coerce.number<number>().int().min(1, "Wert größer 1 erforderlich"),
    formula: z.string().max(200).optional(),
});

const acSchema = z.object({
    value: z.coerce.number<number>().int().min(1, "Wert größer 1 erforderlich"),
    info: z.string().max(200).optional(),
});

const speedsSchema = z.object({
    walk: z.string().max(200),
    fly: z.string().max(200).optional(),
    swim: z.string().max(200).optional(),
    climb: z.string().max(200).optional(),
    burrow: z.string().max(200).optional(),
});

const actionSchema = z.object({
    name: z.string().min(1, "Name erforderlich"),
    type: z.enum(ACTION_TYPES),
    value: z.string().min(1, "Beschreibung erforderlich"),
});


const attributeSchema = z.object({
    stat: z.enum(ATTRIBUTE_STATS),
    value: z.coerce.number<number>().int().min(1).max(30),
    modifier: z.coerce.number<number>().int().min(-10).max(20),
    save: z.coerce.number<number>().int().min(-10).max(20),
});


const spellsSchema = z.array(z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    image: imageSchema.optional(),
})).min(1, "Mindestens ein Zauber ist erforderlich");

const spellcastingFields = {
    attribute: z.enum(ATTRIBUTE_STATS).optional(),
    saveDc: z.coerce.number<number>().int().min(1).max(30).optional(),
    skill: z.coerce.number<number>().int().min(1).max(30).optional(),
    description: z.string().optional(),
};

const spellcastingSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("Klasse"),
        ...spellcastingFields,
        spellGroups: z.array(z.object({
                level: z.coerce.number<number>().int().min(0).max(10),
                slots: z.coerce.number<number>().int().min(0).max(10),
                spells: spellsSchema
            }).refine((group) => group.level !== 0 ? group.slots > 0 : true, {error: "Mindestens ein Slot ist erforderlich"})
        ).min(1, "Mindestens eine Zaubergruppe ist erforderlich"),
    }),
    z.object({
        type: z.literal("Angeboren"),
        ...spellcastingFields,
        spellGroups: z.array(z.object({
            frequency: z.string().max(100).min(1, "Bitte befüllen"),
            spells: spellsSchema
        })).min(1, "Mindestens eine Zaubergruppe ist erforderlich")
    }),
]).nullish();

const stringArray = () => z.array(z.object({value: z.string().min(1, "Bitte befüllen")}))

export const characterSchema = z.object({
    id: z.string().min(3, "ID erforderlich"),
    name: z.string().min(3, "Name erforderlich"),
    hostile: z.boolean(),
    image: imageSchema.optional(),
    type: z.string().optional(),
    race: z.string().optional(),
    alignment: z.string().optional(),
    size: z.string(),
    classes: stringArray(),
    hitPoints: hpSchema,
    armorClass: acSchema,
    speeds: speedsSchema,
    senses: characterSensesSchema,
    languages: stringArray(),
    challenge: z.coerce.number<number>().min(0).max(30),
    xp: z.string().optional(),
    source: z.string().optional(),
    damageResistances: stringArray(),
    damageVulnerabilitys: stringArray(),
    damageImmunitys: stringArray(),
    skills: stringArray(),
    conditionImmunitys: stringArray(),

    attributes: z.array(attributeSchema),
    actions: z.array(actionSchema),
    spellcasting: spellcastingSchema
});

export type CharacterFormInput = z.input<typeof characterSchema>;
export type CharacterFormOutput = z.output<typeof characterSchema>;

export interface SaveCharacterRequest {
    id: string;
    name: string;
    imageId?: string;
    hitPoints: HitPoints;
    speeds: Speeds;
    senses: CharacterSenses;
    hostile: boolean;
    challenge: number;
    xp?: string;
    source?: string;
    alignment?: string;
    languages: string[];
    size: string;
    type?: string;
    race?: string;
    classes: string[];
    armorClass: ArmorClass;
    attributes: Attribute[];
    damageResistances: string[];
    damageVulnerabilitys: string[];
    damageImmunitys: string[];
    skills: string[];
    conditionImmunitys: string[];
    actions: Action[];
    spellcasting?: Spellcasting;
}

export function toCharacterFormValues(character?: Character): CharacterFormInput {
    return {
        id: character?.id ?? "",
        name: character?.name ?? "",
        hostile: character?.hostile ?? true,
        image: character?.image,
        type: character?.type ?? "",
        race: character?.race ?? "",
        size: character?.size ?? "Mittelgroß",
        challenge: character?.challenge ?? 0,
        xp: character?.xp ?? "",
        source: character?.source ?? "",
        alignment: character?.alignment ?? "",
        classes: toFormStringArray(character?.classes),
        languages: toFormStringArray(character?.languages),
        hitPoints: {
            value: character?.hitPoints?.value ?? 1,
            formula: character?.hitPoints?.formula ?? "",
        },
        armorClass: {
            value: character?.armorClass?.value ?? 10,
            info: character?.armorClass?.info ?? "",
        },
        speeds: {
            walk: character?.speeds?.walk ?? "9 m",
            fly: character?.speeds?.fly ?? "",
            swim: character?.speeds?.swim ?? "",
            climb: character?.speeds?.climb ?? "",
            burrow: character?.speeds?.burrow ?? "",
        },
        senses: character ? structuredClone(character.senses) : emptyCharacterSenses(),
        attributes: character?.attributes.map(attribute => ({...attribute})) ?? defaultAttributes(),
        damageResistances: toFormStringArray(character?.damageResistances),
        damageVulnerabilitys: toFormStringArray(character?.damageVulnerabilitys),
        damageImmunitys: toFormStringArray(character?.damageImmunitys),
        conditionImmunitys: toFormStringArray(character?.conditionImmunitys),
        skills: toFormStringArray(character?.skills),
        actions: character?.actions?.map(action => ({...action})) ?? [],
        spellcasting: toFormSpellcasting(character?.spellcasting),
    };
}

export function toSaveCharacterRequest(data: CharacterFormOutput): SaveCharacterRequest {
    return {
        id: data.id,
        name: data.name,
        imageId: data.image?.id,
        hitPoints: normalizeHitPoints(data.hitPoints),
        speeds: normalizeSpeeds(data.speeds),
        senses: data.senses,
        hostile: data.hostile,
        challenge: data.challenge,
        xp: optionalText(data.xp),
        source: optionalText(data.source),
        alignment: optionalText(data.alignment),
        languages: toStringArray(data.languages),
        size: data.size,
        type: optionalText(data.type),
        race: optionalText(data.race),
        classes: toStringArray(data.classes),
        armorClass: {
            value: data.armorClass.value,
            info: optionalText(data.armorClass.info),
        },
        attributes: data.attributes,
        damageResistances: toStringArray(data.damageResistances),
        damageVulnerabilitys: toStringArray(data.damageVulnerabilitys),
        damageImmunitys: toStringArray(data.damageImmunitys),
        skills: toStringArray(data.skills),
        conditionImmunitys: toStringArray(data.conditionImmunitys),
        actions: data.actions,
        spellcasting: data.spellcasting ? normalizeSpellcasting(data.spellcasting) : undefined,
    };
}

export const defaultAttributes = (): Attribute[] => ATTRIBUTE_STATS.map(stat => ({
    stat: stat,
    value: 10,
    modifier: 0,
    save: 0
}))

export const newSpellGroup = (level = 0) => ({spells: [], level, slots: 0, frequency: ""});

const toFormStringArray = (values?: string[] | null) => (values ?? []).map(value => ({value}));
const toStringArray = (values: {value: string}[]) => values.map(item => item.value.trim()).filter(Boolean);
const optionalText = (value?: string | null) => value?.trim() || undefined;

function normalizeHitPoints(hitPoints: HitPoints): HitPoints {
    return {value: hitPoints.value, formula: optionalText(hitPoints.formula)};
}

function normalizeSpeeds(speeds: Speeds): Speeds {
    return {
        walk: speeds.walk,
        fly: optionalText(speeds.fly),
        swim: optionalText(speeds.swim),
        climb: optionalText(speeds.climb),
        burrow: optionalText(speeds.burrow),
    };
}

function toFormSpellcasting(spellcasting?: Spellcasting | null): CharacterFormInput["spellcasting"] {
    if (!spellcasting) return undefined;
    const common = {
        attribute: spellcasting.attribute ?? undefined,
        saveDc: spellcasting.saveDc ?? undefined,
        skill: spellcasting.skill ?? undefined,
        description: spellcasting.description ?? "",
    };
    if (spellcasting.type === "Klasse") return {
        type: "Klasse",
        ...common,
        spellGroups: spellcasting.spellGroups.map(group => ({
            level: group.level ?? 0,
            slots: group.slots ?? 0,
            spells: group.spells.map(spell => ({...spell})),
        })),
    };
    return {
        type: "Angeboren",
        ...common,
        spellGroups: spellcasting.spellGroups.map(group => ({
            frequency: group.frequency ?? "",
            spells: group.spells.map(spell => ({...spell})),
        })),
    };
}

function normalizeSpellcasting(spellcasting: NonNullable<CharacterFormOutput["spellcasting"]>): Spellcasting {
    const common = {
        attribute: spellcasting.attribute,
        saveDc: spellcasting.saveDc,
        skill: spellcasting.skill,
        description: optionalText(spellcasting.description),
    };
    if (spellcasting.type === "Klasse") return {
        type: "Klasse",
        ...common,
        spellGroups: spellcasting.spellGroups.map(group => ({
            level: group.level,
            slots: group.slots,
            spells: group.spells.map(({id, name}) => ({id, name})),
        })),
    };
    return {
        type: "Angeboren",
        ...common,
        spellGroups: spellcasting.spellGroups.map(group => ({
            frequency: group.frequency,
            spells: group.spells.map(({id, name}) => ({id, name})),
        })),
    };
}
